import React, { useState, useEffect, useCallback } from 'react';
import {
  StyleSheet,
  View,
  Text,
  FlatList,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  Dimensions,
  Platform,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigationContext } from '../../navigation/NavigationContext';
import { DriverGridCard } from '../../components/driver/DriverGridCard';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { useDriverStore } from '../../stores/driverStore';
import { useAuthStore } from '../../stores/authStore';
import { PERMISSIONS } from '../../constants/permissions';
import { useResponsiveStyles } from '../../hooks/useResponsiveStyles';
import { AdvancedDriverManagementGuard, FeatureGuard } from '../../features/FeatureGuard';
import { useFeatureEnabled } from '../../features/FeatureFlagProvider';
import { CORE_FEATURES } from '../../features/types';
import { Driver } from '../../types';
import theme from '../../theme';

export const DriversScreen = ({ navigation }: any) => {
  const { setCurrentRoute } = useNavigationContext();
  const { drivers, loading, error, fetchDrivers, removeDriver } = useDriverStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'performance' | 'availability'>('name');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const responsive = useResponsiveStyles();

  // Feature flags
  const isAdvancedDriverManagement = useFeatureEnabled(CORE_FEATURES.ADVANCED_DRIVER_MANAGEMENT);
  const isMobileOptimized = useFeatureEnabled(CORE_FEATURES.MOBILE_OPTIMIZATION);

  useEffect(() => {
    fetchDrivers();
  }, []);

  // Responsive columns: Mobile: 2, Web: 4
  const numColumns = responsive.isWeb ? 4 : 2;

  // Enhanced filtering and sorting with performance analytics
  const filteredDrivers = drivers
    // FIRST: Deduplicate drivers by ID (prevents showing same driver twice in partnerships)
    .filter((driver, index, self) => 
      index === self.findIndex((d) => d.id === driver.id)
    )
    // THEN: Apply search and status filters
    .filter((driver) => {
      const matchesSearch =
        !searchQuery ||
        driver.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        driver.licenseNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        driver.nationality?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = !statusFilter || driver.status === statusFilter;

      return matchesSearch && matchesStatus;
    })
    // FINALLY: Sort by selected criteria
    .sort((a, b) => {
      if (sortBy === 'performance' && isAdvancedDriverManagement) {
        // Mock performance score - in real app, calculate from job history
        const aPerformance = Math.random() * 100;
        const bPerformance = Math.random() * 100;
        return bPerformance - aPerformance;
      }
      
      if (sortBy === 'availability') {
        const aIsAvailable = a.status === 'inactive';
        const bIsAvailable = b.status === 'inactive';
        
        if (aIsAvailable && !bIsAvailable) return -1;
        if (!aIsAvailable && bIsAvailable) return 1;
      }
      
      // Default sort by name
      return (a.name || '').localeCompare(b.name || '');
    });

  // Handle refresh
  const onRefresh = async () => {
    setRefreshing(true);
    await fetchDrivers();
    setRefreshing(false);
  };

  // Enhanced driver actions with feature flag support
  const handleDriverPress = (driver: Driver) => {
    navigation.navigate('DriverDetails', { driverId: driver.id });
  };

  const handleEditDriver = (driver: Driver) => {
    navigation.navigate('DriverDetails', { driverId: driver.id, mode: 'edit' });
  };

  const handleDeleteDriver = (driver: Driver) => {
    // Check user permission for DELETE_DRIVERS
    const hasDeletePermission = useAuthStore.getState().hasPermission(PERMISSIONS.DELETE_DRIVERS);
    
    if (!hasDeletePermission) {
      if (Platform.OS === 'web') {
        alert('ليس لديك صلاحية لحذف السائقين\nYou do not have permission to delete drivers');
      } else {
        Alert.alert(
          'صلاحية مطلوبة / Permission Required',
          'ليس لديك صلاحية لحذف السائقين\nYou do not have permission to delete drivers',
          [{ text: 'حسناً / OK' }]
        );
      }
      return;
    }
    
    if (Platform.OS === 'web') {
      const confirmed = window.confirm(
        `هل أنت متأكد من حذف ${driver.name}?\nAre you sure you want to delete ${driver.name}?`
      );
      if (confirmed) {
        console.log('Delete driver:', driver.id);
        removeDriver(driver.id).then(() => {
          console.log('✅ Driver deleted successfully');
          fetchDrivers(); // Refresh the list
        }).catch((error) => {
          console.error('❌ Delete failed:', error);
          alert('Failed to delete driver: ' + error.message);
        });
      }
    } else {
      Alert.alert(
        'حذف السائق / Delete Driver',
        `هل أنت متأكد من حذف ${driver.name}?\nAre you sure you want to delete ${driver.name}?`,
        [
          { text: 'إلغاء / Cancel', style: 'cancel' },
          { text: 'حذف / Delete', style: 'destructive', onPress: async () => {
            try {
              console.log('Delete driver:', driver.id);
              await removeDriver(driver.id);
              console.log('✅ Driver deleted successfully');
              await fetchDrivers(); // Refresh the list
            } catch (error: any) {
              console.error('❌ Delete failed:', error);
              Alert.alert('Error', 'Failed to delete driver: ' + error.message);
            }
          }}
        ]
      );
    }
  };

  const handleAssignVehicle = (driver: Driver) => {
    // Navigate to Assignment section with BilingualAssignmentForm
    if (Platform.OS === 'web') {
      // Web: Use custom navigation context
      sessionStorage.setItem('assignmentPreSelection', JSON.stringify({
        preSelectedDriverId: driver.id,
        preSelectedFormType: 'delivery'
      }));
      setCurrentRoute('Assignments');
    } else {
      // Mobile: Use React Navigation
      navigation.navigate('Assignments', {
        screen: 'AssignmentMain',
        params: {
          preSelectedDriverId: driver.id,
          preSelectedFormType: 'delivery'
        }
      });
    }
  };

  const handleUnassignVehicle = (driver: Driver) => {
    // Navigate to Assignment section with BilingualAssignmentForm for receiving
    if (Platform.OS === 'web') {
      // Web: Use custom navigation context
      sessionStorage.setItem('assignmentPreSelection', JSON.stringify({
        preSelectedDriverId: driver.id,
        preSelectedFormType: 'receiving'
      }));
      setCurrentRoute('Assignments');
    } else {
      // Mobile: Use React Navigation
      navigation.navigate('Assignments', {
        screen: 'AssignmentMain',
        params: {
          preSelectedDriverId: driver.id,
          preSelectedFormType: 'receiving'
        }
      });
    }
  };

  const handleBulkAction = (action: 'export' | 'assign' | 'status') => {
    if (action === 'export') {
      navigation.navigate('DriverExport');
    } else {
      Alert.alert(
        'Bulk Action',
        `${action} feature will be implemented in the next update!`,
        [{ text: 'OK' }]
      );
    }
  };

  // Enhanced header with analytics and controls
  const renderEnhancedHeader = () => (
    <AdvancedDriverManagementGuard>
      <Card style={styles.analyticsCard}>
        <View style={styles.analyticsHeader}>
          <Text style={styles.analyticsTitle}>Driver Analytics</Text>
          <TouchableOpacity onPress={() => navigation.navigate('DriverAnalytics')}>
            <Text style={styles.viewDetailsText}>View Details</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.analyticsRow}>
          <View style={styles.analyticsStat}>
            <Text style={styles.analyticsValue}>{drivers.filter(d => d.status === 'active' && !d.currentVehicleId).length}</Text>
            <Text style={styles.analyticsLabel}>Available</Text>
          </View>
          <View style={styles.analyticsStat}>
            <Text style={styles.analyticsValue}>{drivers.filter(d => d.currentVehicleId).length}</Text>
            <Text style={styles.analyticsLabel}>Assigned</Text>
          </View>
          <View style={styles.analyticsStat}>
            <Text style={styles.analyticsValue}>
              {drivers.length > 0 ? Math.round((drivers.filter(d => d.currentVehicleId).length / drivers.length) * 100) : 0}%
            </Text>
            <Text style={styles.analyticsLabel}>Utilization</Text>
          </View>
          <View style={styles.analyticsStat}>
            <Text style={styles.analyticsValue}>4.6</Text>
            <Text style={styles.analyticsLabel}>Avg Rating</Text>
          </View>
        </View>
      </Card>
    </AdvancedDriverManagementGuard>
  );

  // Enhanced controls with sorting and view options
  const renderEnhancedControls = () => (
    <AdvancedDriverManagementGuard>
      <View style={styles.controlsContainer}>
        <View style={styles.sortingContainer}>
          <Text style={styles.controlLabel}>Sort by:</Text>
          <View style={styles.sortButtons}>
            {[
              { key: 'name', label: 'Name' },
              { key: 'performance', label: 'Performance' },
              { key: 'availability', label: 'Available' },
            ].map((option) => (
              <TouchableOpacity
                key={option.key}
                style={[
                  styles.sortButton,
                  sortBy === option.key && styles.sortButtonActive,
                ]}
                onPress={() => setSortBy(option.key as any)}
              >
                <Text style={[
                  styles.sortButtonText,
                  sortBy === option.key && styles.sortButtonTextActive,
                ]}>
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
        
        <View style={styles.viewModeContainer}>
          <TouchableOpacity
            style={[styles.viewModeButton, viewMode === 'grid' && styles.viewModeButtonActive]}
            onPress={() => setViewMode('grid')}
          >
            <Ionicons name="grid-outline" size={20} color={
              viewMode === 'grid' ? theme.colors.white : theme.colors.gray600
            } />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.viewModeButton, viewMode === 'list' && styles.viewModeButtonActive]}
            onPress={() => setViewMode('list')}
          >
            <Ionicons name="list-outline" size={20} color={
              viewMode === 'list' ? theme.colors.white : theme.colors.gray600
            } />
          </TouchableOpacity>
        </View>
      </View>
    </AdvancedDriverManagementGuard>
  );

  // Enhanced action buttons
  // Enhanced action buttons
  const renderActionButtons = () => (
    <View style={styles.actionButtonsContainer}>
      <Button
        title="Add Driver"
        variant="primary"
        size="small"
        leftIcon={<Ionicons name="person-add-outline" size={16} color="white" />}
        onPress={handleAddDriver}
        style={styles.actionButton}
      />
      
      <AdvancedDriverManagementGuard>
        <Button
          title="Bulk Actions"
          variant="secondary"
          size="small"
          leftIcon={<Ionicons name="options-outline" size={16} color="white" />}
          onPress={() => handleBulkAction('assign')}
          style={styles.actionButton}
        />
        <Button
          title="Export"
          variant="outline"
          size="small"
          leftIcon={<Ionicons name="download-outline" size={16} color={theme.colors.primary} />}
          onPress={() => handleBulkAction('export')}
          style={styles.actionButton}
        />
      </AdvancedDriverManagementGuard>
    </View>
  );

  // Handle filter by status
  const handleFilterByStatus = (status: string | null) => {
    setStatusFilter(status === statusFilter ? null : status);
  };

  // Handle add new driver
  const handleAddDriver = () => {
    // Navigate to add driver screen
    navigation.navigate('AddDriver');
  };

  // Status filter buttons
  const renderStatusFilters = () => {
    const statuses = [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
      { value: 'vacation', label: 'Vacation' },
      { value: 'suspended', label: 'Suspended' },
    ];

    return (
      <View style={styles.filtersContainer}>
        <FlatList
          horizontal
          showsHorizontalScrollIndicator={false}
          data={statuses}
          keyExtractor={(item) => item.value}
          contentContainerStyle={styles.statusFilters}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={[
                styles.filterButton,
                statusFilter === item.value && styles.filterButtonActive,
              ]}
              onPress={() => handleFilterByStatus(item.value)}
            >
              <Text
                style={[
                  styles.filterButtonText,
                  statusFilter === item.value && styles.filterButtonTextActive,
                ]}
              >
                {item.label}
              </Text>
            </TouchableOpacity>
          )}
        />
      </View>
    );
  };

  // Render empty state
  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <Ionicons name="person-outline" size={64} color={theme.colors.gray300} />
      <Text style={styles.emptyTitle}>No Drivers Found</Text>
      <Text style={styles.emptyDescription}>
        {searchQuery || statusFilter
          ? 'Try adjusting your search or filters'
          : 'Add your first driver to get started'}
      </Text>
      {!searchQuery && !statusFilter && (
        <Button
          title="Add Driver"
          onPress={handleAddDriver}
          style={styles.emptyButton}
        />
      )}
    </View>
  );

  return (
    <View style={styles.container}>
      {/* Driver List with Header Content */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      ) : (
        <FlatList
          data={filteredDrivers}
          renderItem={({ item }) => (
            <DriverGridCard 
              driver={item} 
              onPress={() => handleDriverPress(item)}
              onViewDetails={() => handleDriverPress(item)}
              onEdit={() => handleEditDriver(item)}
              onDelete={() => handleDeleteDriver(item)}
              onAssign={() => handleAssignVehicle(item)}
              onUnassign={() => handleUnassignVehicle(item)}
              viewMode={viewMode}
            />
          )}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.gridContainer}
          numColumns={viewMode === 'grid' ? numColumns : 1}
          key={`${numColumns}-${viewMode}`} // Force re-render when layout changes
          ListHeaderComponent={
            <>
              {/* Enhanced Analytics Header */}
              {renderEnhancedHeader()}

              {/* Search Container */}
              <View style={styles.searchContainer}>
                <Ionicons name="search" size={20} color={theme.colors.gray400} style={styles.searchIcon} />
                <TextInput
                  style={styles.searchInput}
                  placeholder="Search by name, ID or nationality..."
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  clearButtonMode="while-editing"
                />
                {searchQuery ? (
                  <TouchableOpacity onPress={() => setSearchQuery('')}>
                    <Ionicons name="close-circle" size={20} color={theme.colors.gray400} />
                  </TouchableOpacity>
                ) : null}
              </View>

              {/* Enhanced Controls */}
              {renderEnhancedControls()}

              {/* Status Filters */}
              {renderStatusFilters()}

              {/* Action Buttons */}
              {renderActionButtons()}

              {/* Driver Count */}
              <View style={styles.countContainer}>
                <Text style={styles.countText}>
                  {filteredDrivers.length} of {drivers.length} drivers
                </Text>
                
                <AdvancedDriverManagementGuard>
                  <Text style={styles.performanceText}>
                    Sorted by {sortBy} • {viewMode} view
                  </Text>
                </AdvancedDriverManagementGuard>
              </View>
            </>
          }
          ListEmptyComponent={renderEmpty}
          refreshControl={
            <RefreshControl refreshing={loading || refreshing} onRefresh={onRefresh} colors={[theme.colors.primary]} />
          }
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  analyticsCard: {
    margin: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  analyticsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  analyticsTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
  },
  viewDetailsText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.primary,
    fontWeight: '500' as any,
  },
  analyticsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  analyticsStat: {
    alignItems: 'center',
    flex: 1,
  },
  analyticsValue: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.primary,
  },
  analyticsLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginTop: theme.spacing.xs,
  },
  controlsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  sortingContainer: {
    flex: 1,
  },
  controlLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xs,
  },
  sortButtons: {
    flexDirection: 'row',
  },
  sortButton: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    marginRight: theme.spacing.xs,
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.gray100,
  },
  sortButtonActive: {
    backgroundColor: theme.colors.primary,
  },
  sortButtonText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  sortButtonTextActive: {
    color: theme.colors.white,
  },
  viewModeContainer: {
    flexDirection: 'row',
    backgroundColor: theme.colors.gray100,
    borderRadius: theme.borderRadius.sm,
    padding: 2,
  },
  viewModeButton: {
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.sm,
  },
  viewModeButtonActive: {
    backgroundColor: theme.colors.primary,
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    paddingHorizontal: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  actionButton: {
    marginRight: theme.spacing.sm,
  },
  performanceText: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
    fontStyle: 'italic',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.white,
    ...theme.shadows.sm,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
  },
  countContainer: {
    paddingHorizontal: theme.spacing.md,
    paddingBottom: theme.spacing.xs,
  },
  countText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    marginHorizontal: theme.spacing.md,
    marginVertical: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
  },
  searchIcon: {
    marginRight: theme.spacing.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textPrimary,
    paddingVertical: theme.spacing.xs,
  },
  filtersContainer: {
    marginBottom: theme.spacing.md,
  },
  statusFilters: {
    paddingHorizontal: theme.spacing.md,
  },
  filterButton: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    marginRight: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.gray100,
  },
  filterButtonActive: {
    backgroundColor: theme.colors.primary,
  },
  filterButtonText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  filterButtonTextActive: {
    color: theme.colors.white,
  },
  listContainer: {
    padding: theme.spacing.sm,
    paddingBottom: theme.spacing.xl,
  },
  gridContainer: {
    padding: theme.spacing.sm,
    paddingBottom: theme.spacing.xl,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.xl,
    marginTop: theme.spacing.xl,
  },
  emptyTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginTop: theme.spacing.md,
  },
  emptyDescription: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: theme.spacing.sm,
    marginBottom: theme.spacing.lg,
  },
  emptyButton: {
    marginTop: theme.spacing.md,
  },
});
